import { DeletedEmsCmd } from '../em/deleted-ems.cmd';
import { Model } from '../../model/model';
import { DataPack, EntityModificationTrace, WorkCom } from '@wizeoo/akm-message-lib';
import { take } from 'rxjs';
import { ViewData } from '../../model/view.data';
import { AkmBDD, BddWorkcom } from '@wizeoo/akm-worker-lib';
import { AllkeyWebWorker } from '../../manager/worker/allkey.web.worker';
import { WizCommand, WizGroupCommand } from '@wizeoo/akm-component-lib';
import { WizConstants, WorkComVO} from '@wizeoo/akm-compute-lib';
import { WorkcomDataload } from '../../vo/workflow/workcom.dataload';
import { WorkcomCountCmd } from './workcomcount.cmd';
import { WorkcomCmd } from './workcom.cmd';

/** service de gestion des Workcoms. Synchro cache local et appels par incréments */
export class WorkcomSyncService {

  protected model: Model = null;

  constructor(model: Model) {
    this.model = model;
  }

  // pack de chargement
  public dataload: WorkcomDataload = null; 

  /** la clé du pack, domain + BU */
  public get key(): string {
    return [this.model.domainCode, this.model.businessUnitCode].join(ViewData.KEY_SEPARATOR);
  }

  public get visionKey(): string {
    return this.model.visionCode;
  }

  public resetAll(): void {
    this.dataload = null;
    this.model.workflowEngine.reset();
  }

  /**
   * chargement des Workcoms depuis le cache local & synchro remote
   * @param callback pour retour du pool Workcoms: []
   * @param force si actif, force le rechargement (sur import)
   * @returns Workcoms: []
   */
  public findWorkcoms(callback: Function, force: boolean = false): void {
    
    const key: string = this.key;
    const visionKey: string = this.visionKey;
    const uniqueKey: string = [key, visionKey].join(WizConstants.KEY_SEPARATOR);
    if (!!this.dataload) { // j'en ai déjà un, je contrôle sa clé (init multi BU...)   
      if (this.dataload.uniqueKey !== uniqueKey) {
        this.dataload = null; // j'en ai plus...
      }
    }
    
    if (!force) { // je suis pas en train de forcer
      if (this.dataload?.state === WorkcomDataload.COMPLETE) { // et il est déjà complet
        callback(this.dataload.localWcs); // je renvoie direct le pack
        return;
      }
    }

    if (!this.dataload || force) { // pas de pack, ou demande de forçage
      this.dataload = new WorkcomDataload(this.model.domainCode, this.model.businessUnitCode, this.model.visionCode);
      this.dataload.callbacks.push(callback); //  = callback;

      // chargement local
      AkmBDD.getInstance().doFindWorkcoms(uniqueKey)
        .pipe(take(1))
        .subscribe((bddWorkcoms: BddWorkcom[]) => {
        

        let locaux: Map<string, WorkCom> = new Map();
        let local: WorkCom;
        let localRevisionNumber: number = -1;

        // TODO gestion des revNumbers par FD    
        if(!!bddWorkcoms) {
          // console.log('fds', bddFDs);
          bddWorkcoms.forEach((bddwc: BddWorkcom) => {
            local = bddwc.value;
            locaux.set(bddwc.key, local);
            if(local.revisionNumber > localRevisionNumber) {
              localRevisionNumber = local.revisionNumber;
            }
          });
        }

        if (localRevisionNumber >= 0) { // alimentation du numRev
          this.dataload.revisionNumber = localRevisionNumber;
        }
        this.dataload.localWcs = locaux; // alimentation du jeu local dans le pack

        // console.log('fd service', this.dataload.revisionNumber);
        new WorkcomCountCmd(this.model).count(this.dataload, this.backFromCount.bind(this)).execute();
      });
    } else { // j'ai déjà un pack en cours, non COMPLETE... J'attends
      this.dataload.callbacks.push(callback); //  = callback;
    }
  }

  /**
   * retour de count, éventuel appel remote
   * @param cmd 
   */
  private backFromCount(cmd: WorkcomCountCmd): void {
    this.remoteStep(cmd.dataLoad);
  }

  /**
   * pile d'appels remote
   * @param dataload 
   */
  private remoteStep(dataload: WorkcomDataload): void {
    // console.log('WorkcomDataload remoteStep', dataload.nbFds, dataload.nbFdvs, dataload.nbDeleteds);
    const cmds: WizCommand<any>[] = [];

    if (dataload.nbWorkcoms > 0) { // des FDs à mettre à jour
      cmds.push(new WorkcomCmd(this.model).findFromRevNumber(dataload)); // uniquement les entêtes... les FDV sont gérés à part
    }

    if (dataload.nbDeleteds > 0) { // et peut-être des deletes
      cmds.push(new DeletedEmsCmd(this.model).findWorkcom(dataload));
    }

    if (cmds.length) {
      const grpCmd = new WizGroupCommand().configure(cmds, this.backFromFind.bind(this), true);
      grpCmd.carrier = dataload;
      grpCmd.execute();
    } else {
      // console.log('FD remote', dataload);
      this.endService(dataload);
    }
  }

  private backFromFind(cmd: WizGroupCommand): void {
    this.endService(cmd.carrier);
  }

  /** alimentation des collections et merge local */
  private endService(dataLoad: WorkcomDataload): void {
    this.dataload = dataLoad;
    this.dataload.state = WorkcomDataload.COMPLETE; // ok, chargé

    const voMap: Map<string, WorkComVO> = new Map(); // alimentation des WorkComVOs
    const updatedSet: Map<string, WorkCom> = new Map<string, WorkCom>(); // lister les WorkComs modifiés

    const toDieSet: Map<number, EntityModificationTrace> = new Map(); // je vois des gens qui sont morts..
    if(!!this.dataload.deletedWorkComs) {
      this.dataload.deletedWorkComs.forEach((emt: EntityModificationTrace) => {
        toDieSet.set(emt.id, emt);
      });
    }

    this.dataload.localWcs.forEach((wc: WorkCom) => { // on garde les pas morts
      if (!toDieSet.has(wc.id)) { // dans la map des morts, au revoir
        voMap.set(wc.naturalUID, new WorkComVO(this.model, wc));
      }
    });

    this.dataload.workcoms.forEach((wc: WorkCom) => { // on écrase avec les updates, on crée les nouveaux
      const exist: WorkComVO =  voMap.get(wc.naturalUID); 
      if(!!exist) {
        
      }
      voMap.set(wc.naturalUID, new WorkComVO(this.model, wc));
      updatedSet.set(wc.naturalUID, wc);
    });

  
    const sourceArr: Map<string, WorkCom> = new Map(); // la collec des FDs encore vivants, et les nouveaux, pour alimentation cache local
    // construction de la map à sauvegarder
    [...voMap.values()].forEach((wcvo: WorkComVO) => {
      sourceArr.set(wcvo.naturalUID, wcvo.source);
    });

    this.dataload.localWcs = sourceArr; // on met à jour le dataLoad, il est clean

    this.model.workflowEngine.setWorkcomCollection(Array.from(voMap.values())); // on alimente le modèle
    this.model.workflowEngine.setWorkcomMap(voMap);

    if (this.dataload.remoteRevisionNumber > this.dataload.revisionNumber) {
      
      // mise à jour du dataLoad
      this.dataload.revisionNumber = this.dataload.remoteRevisionNumber; // sync directe
      this.dataload.toPersist = updatedSet;
      this.dataload.toDie = toDieSet;
      this.writeBDD(this.dataload);
    }
    this.resolveCallbacks();

  }

  /** on remercie les callbacks, en leur passant le pool local (source) */
  private resolveCallbacks(): void {
    this.dataload.callbacks.forEach((f: Function) => {
      f(this.dataload.localWcs);
    })
    this.dataload.callbacks = []; // on rince les callbacks;
  }

  /** persistence local par le serviceWorker */
  private writeBDD(dataload: WorkcomDataload): void {
    AllkeyWebWorker.storeWorkcom(dataload);
  }

  /**
   * suppression d'un FD depuis la vue FreedataView
   */
  public delete(wc: WorkComVO): void {
    
    this.dataload.toDie = new Map();
    this.dataload.toDie.set(wc.id, null);
    this.dataload.toPersist = null;

    this.dataload.localWcs.delete(wc.naturalUID); // suppression du dataLoad
    AllkeyWebWorker.storeWorkcom(this.dataload); // suppression en BDD
  }

  /**
   * creation d'un Workcom 
   */
    public add(wc: WorkComVO): void {
    
      this.dataload.toDie = null;
      this.dataload.toPersist = new Map();
      this.dataload.toPersist.set(wc.naturalUID, wc.source);
  
      this.dataload.localWcs.set(wc.naturalUID, wc.source); // ajout du dataLoad

      AllkeyWebWorker.storeWorkcom(this.dataload); // ajout en BDD
    }


  /** 
    * sur retour de commit, mise à jour du cache et des dataload chargés
    * @param pack 
    */
  public eatRemote(pack: DataPack): void 
  {
    let localMap: Map<string, WorkCom> = this.dataload.localWcs; // les wcs stockées en indexDB
    // map des wcs modifiés
    const toUpdateWC: Map<string, WorkCom> = new Map<string, WorkCom>();
    // map des wcs à supprimer, par ID
    const toDieWC: Map<number, EntityModificationTrace> = new Map(); 

    // obj en cours de manipulation
    let myWC: WorkCom;
    // gestion des deleted en premier, en cas de delete recreate
    if(pack.deletedWorkCom) {

      pack.deletedWorkCom.forEach((emt: EntityModificationTrace) => {
          toDieWC.set(emt.id, emt); // mémorisation pour nettoyer en cache local
          myWC = localMap.get(emt.key);
          if(!!myWC) {
            this.model.workflowEngine.removeWorkCom(myWC.naturalUID);
          }
          localMap.delete(emt.key); // suppression du dataload
      });
    }
    
    if(pack.remoteWorkCom) { // updates / create sur FD
      pack.remoteWorkCom.forEach((wc: WorkCom) => {
        myWC = localMap.get(wc.naturalUID);
        if(!!myWC) { // update
          this.model.workflowEngine.getWorkCom(wc.naturalUID).source = wc;
        } else { // create
          this.model.workflowEngine.addWorkCom(new WorkComVO(this.model, wc));
        }
        
        localMap.set(wc.naturalUID, wc );
        toUpdateWC.set(wc.naturalUID, wc);        

      });
    }

    // synchro du dataload
    this.dataload.localWcs = localMap; 

    if(!pack.crossGenerated) { // crossVision on ne monte pas la version, on a pas collecté ce qui à pu être généré
      this.dataload.remoteRevisionNumber = pack.workcomRevisionNumber;
      this.dataload.revisionNumber = this.dataload.remoteRevisionNumber;
    }
  
    this.dataload.toPersist = toUpdateWC;
    this.dataload.toDie = toDieWC;
    this.writeBDD(this.dataload);

  }
}
